package br.com.htms.inspeccaocampo.data

import br.com.htms.inspeccaocampo.data.checklist.semConteudoBase64
import br.com.htms.inspeccaocampo.platform.PlatformFiles
import br.com.htms.inspeccaocampo.platform.currentTimeMillis
import br.com.htms.inspeccaocampo.util.Base64Codec

/** Resultado de gravar inspeção no armazenamento durável. */
data class SalvarInspecaoResult(
    val inspecao: InspecaoCompleta,
    /** true se IndexedDB/disco confirmou a gravação. */
    val gravouCache: Boolean,
    /** true se removeu conteudoBase64 para caber na quota (paths das fotos mantidos). */
    val usouJsonEnxuto: Boolean = false,
    val erro: String? = null
)

class InspecaoStore {
    private val dir: String
        get() = PlatformFiles.join(PlatformFiles.documentsDir(), "inspecoes").also {
            PlatformFiles.ensureDir(it)
        }

    fun listar(): List<InspecaoCompleta> =
        PlatformFiles.listFiles(dir)
            .filter { it.endsWith(".json") }
            .mapNotNull { path ->
                runCatching {
                    FormJson.inspecaoFromJson(PlatformFiles.readText(path) ?: return@mapNotNull null)
                }.getOrNull()
            }
            .sortedByDescending { it.criadoEm }

    /**
     * Persiste a inspeção aguardando escrita durável (web: IndexedDB).
     * Se o JSON completo (com base64 das fotos) estourar a quota, grava versão enxuta
     * só com metadados + paths — as fotos já estão em chaves separadas no IDB.
     */
    suspend fun salvar(form: FormInspecao, idEdicao: Long?): SalvarInspecaoResult =
        salvarCompleta(
            InspecaoCompleta(
                id = if (idEdicao != null && idEdicao > 0) idEdicao else currentTimeMillis(),
                criadoEm = currentTimeMillis(),
                formTipo = FormTipo.INSPECAO_CAMPO,
                form = form
            ),
            idEdicao
        )

    suspend fun salvarCompleta(inspecaoBase: InspecaoCompleta, idEdicao: Long?): SalvarInspecaoResult {
        val agora = currentTimeMillis()
        val inspecao = if (idEdicao != null && idEdicao > 0) {
            inspecaoBase.copy(id = idEdicao, criadoEm = agora)
        } else {
            inspecaoBase.copy(
                id = if (inspecaoBase.id > 0) inspecaoBase.id else agora,
                criadoEm = agora
            )
        }
        val path = PlatformFiles.join(dir, "${inspecao.id}.json")
        persistirFotosSeparadas(inspecao)
        val fullJson = FormJson.inspecaoToJson(inspecao)
        if (PlatformFiles.writeTextDurable(path, fullJson)) {
            return SalvarInspecaoResult(inspecao = inspecao, gravouCache = true)
        }
        val enxuta = when (inspecao.formTipo) {
            FormTipo.INSPECAO_CAMPO ->
                inspecao.copy(form = inspecao.form.semConteudoBase64())
            FormTipo.RELATORIO_FOTO_ALOJAMENTO ->
                inspecao.copy(relatorioFoto = inspecao.relatorioFoto?.semConteudoBase64())
            FormTipo.CHECKLIST_ALOJAMENTO,
            FormTipo.CHECKLIST_VETORES -> inspecao
            FormTipo.RELATORIO_ALOJAMENTO ->
                inspecao.copy(relatorioFoto = inspecao.relatorioFoto?.semConteudoBase64())
            FormTipo.COMUNICACAO_OCORRENCIAS ->
                inspecao.copy(comunicacaoOcorrencias = inspecao.comunicacaoOcorrencias?.semConteudoBase64())
            FormTipo.CHECKLIST_DIARIO_HTMS,
            FormTipo.CHECKLIST_DIARIO_AXIA ->
                inspecao.copy(checklistDiario = inspecao.checklistDiario?.semConteudoBase64())
            FormTipo.REM_SEGURANCA_OBRAS,
            FormTipo.RELATORIO_SEGURANCA,
            FormTipo.ACOMPANHAMENTO_AMBIENTAL,
            FormTipo.EVIDENCIA_TREINAMENTO -> inspecao
            FormTipo.ANALISE_VISUAL_NR ->
                inspecao.copy(analiseVisualNr = inspecao.analiseVisualNr?.semConteudoBase64())
        }
        val slimJson = FormJson.inspecaoToJson(enxuta)
        if (PlatformFiles.writeTextDurable(path, slimJson)) {
            persistirFotosSeparadas(inspecao)
            // Mantém Base64 na memória/PDF mesmo se o JSON em disco ficou enxuto.
            return SalvarInspecaoResult(
                inspecao = inspecao,
                gravouCache = true,
                usouJsonEnxuto = true
            )
        }
        return SalvarInspecaoResult(
            inspecao = inspecao,
            gravouCache = false,
            erro = "Falha ao gravar no cache do navegador (IndexedDB)."
        )
    }

    /** JPEG em arquivo próprio (IDB/localStorage) — o JSON enxuto só guarda o path. */
    private suspend fun persistirFotosSeparadas(inspecao: InspecaoCompleta) {
        inspecao.fotosAnexadas().forEach { foto ->
            if (foto.path.isBlank()) return@forEach
            val jaTem = PlatformFiles.readBytes(foto.path)?.takeIf { it.isNotEmpty() }
            if (jaTem != null) {
                PlatformFiles.writeBytesDurable(foto.path, jaTem)
                return@forEach
            }
            val b64 = foto.conteudoBase64?.takeIf { it.isNotBlank() } ?: return@forEach
            val bytes = Base64Codec.decode(b64)?.takeIf { it.isNotEmpty() } ?: return@forEach
            PlatformFiles.writeBytesDurable(foto.path, bytes)
        }
    }

    fun excluir(id: Long) {
        PlatformFiles.delete(PlatformFiles.join(dir, "$id.json"))
    }

    /** Atualiza JSON sem alterar id/criadoEm (ex.: marcar enviadoRemoto). */
    suspend fun atualizar(inspecao: InspecaoCompleta): Boolean {
        if (inspecao.id <= 0) return false
        val path = PlatformFiles.join(dir, "${inspecao.id}.json")
        return PlatformFiles.writeTextDurable(path, FormJson.inspecaoToJson(inspecao))
    }
}

data class RascunhoAlojamento(
    val formTipo: String,
    val username: String,
    val relatorio: FormRelatorioFoto,
    val checklist: FormChecklistAlojamento
)

class DraftStore {
    private val path: String
        get() = PlatformFiles.join(PlatformFiles.documentsDir(), "rascunho_inspecao.json")
    private val checklistDiarioPath: String
        get() = PlatformFiles.join(PlatformFiles.documentsDir(), "rascunho_checklist_diario.json")

    suspend fun salvar(form: FormInspecao, idEdicao: Long?): Boolean {
        // Sempre grava rascunho sem base64 das fotos (paths no IDB bastam) — evita OOM/quota.
        val slim = jsonRascunhoInspecao(form, idEdicao)
        return PlatformFiles.writeTextDurable(path, slim)
    }

    fun salvarInspecaoImediato(form: FormInspecao, idEdicao: Long?) {
        PlatformFiles.writeText(path, jsonRascunhoInspecao(form, idEdicao))
    }

    private fun jsonRascunhoInspecao(form: FormInspecao, idEdicao: Long?): String = buildString {
        append('{')
        if (idEdicao != null) append("\"idEdicao\":$idEdicao,") else append("\"idEdicao\":null,")
        append("\"form\":")
        append(FormJson.toJson(form.semConteudoBase64()))
        append('}')
    }

    fun carregar(): Pair<FormInspecao, Long?>? {
        val text = PlatformFiles.readText(path) ?: return null
        return runCatching {
            val root = JsonMap.parse(text)
            val formNode = root.obj("form") ?: return null
            FormJson.fromJson(formNode.raw) to root.long("idEdicao")
        }.getOrNull()
    }

    fun limpar() {
        PlatformFiles.delete(path)
    }

    /** Rascunho independente para os checklists HTMS/Axia, sem base64 para preservar quota. */
    suspend fun salvarChecklistDiario(
        form: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario,
        idEdicao: Long?
    ): Boolean {
        val json = jsonRascunhoChecklistDiario(form, idEdicao)
        return PlatformFiles.writeTextDurable(checklistDiarioPath, json)
    }

    fun salvarChecklistDiarioImediato(
        form: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario,
        idEdicao: Long?
    ) {
        PlatformFiles.writeText(checklistDiarioPath, jsonRascunhoChecklistDiario(form, idEdicao))
    }

    private fun jsonRascunhoChecklistDiario(
        form: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario,
        idEdicao: Long?
    ): String {
        val slim = form.semConteudoBase64()
        return buildString {
            append('{')
            if (idEdicao != null) append("\"idEdicao\":$idEdicao,") else append("\"idEdicao\":null,")
            append("\"form\":")
            append(FormJson.checklistDiarioToJson(slim))
            append('}')
        }
    }

    fun carregarChecklistDiario(): Pair<br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario, Long?>? {
        val text = PlatformFiles.readText(checklistDiarioPath) ?: return null
        return runCatching {
            val root = JsonMap.parse(text)
            val formNode = root.obj("form") ?: return null
            FormJson.checklistDiarioFromJson(formNode.raw) to root.long("idEdicao")
        }.getOrNull()
    }

    fun limparChecklistDiario() {
        PlatformFiles.delete(checklistDiarioPath)
    }

    private val alojamentoPath: String
        get() = PlatformFiles.join(PlatformFiles.documentsDir(), "rascunho_alojamento.json")

    /**
     * Rascunho do Alojamento (Parte 1 fotográfico + Parte 2 checklist), sem base64:
     * as fotos seguem gravadas pelo path (disco / IndexedDB).
     */
    fun salvarAlojamento(
        formTipo: String,
        username: String,
        relatorio: FormRelatorioFoto,
        checklist: FormChecklistAlojamento
    ) {
        val json = buildString {
            append("{\"formTipo\":\"").append(formTipo).append("\",")
            append("\"username\":").append(jsonQuote(username)).append(',')
            append("\"relatorio\":").append(FormJson.relatorioToJson(relatorio.semConteudoBase64())).append(',')
            append("\"checklist\":").append(FormJson.checklistToJson(checklist))
            append('}')
        }
        PlatformFiles.writeText(alojamentoPath, json)
    }

    fun carregarAlojamento(): RascunhoAlojamento? {
        val text = PlatformFiles.readText(alojamentoPath) ?: return null
        return runCatching {
            val root = JsonMap.parse(text)
            RascunhoAlojamento(
                formTipo = root.str("formTipo"),
                username = root.str("username"),
                relatorio = root.obj("relatorio")?.let { FormJson.relatorioFromJson(it.raw) } ?: return null,
                checklist = root.obj("checklist")?.let {
                    val base = if (root.str("formTipo") == FormTipo.CHECKLIST_VETORES.id) {
                        ChecklistVetoresCatalog.itens()
                    } else {
                        ChecklistAlojamentoCatalog.itensPadrao()
                    }
                    FormJson.checklistFromJson(it.raw, base)
                } ?: return null
            )
        }.getOrNull()
    }

    fun limparAlojamento() {
        PlatformFiles.delete(alojamentoPath)
    }

    private fun jsonQuote(s: String): String = buildString {
        append('"')
        s.forEach { c ->
            when (c) {
                '"' -> append("\\\"")
                '\\' -> append("\\\\")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> if (c < ' ') append("\\u" + c.code.toString(16).padStart(4, '0')) else append(c)
            }
        }
        append('"')
    }

    private val formularioAtivoPath: String
        get() = PlatformFiles.join(PlatformFiles.documentsDir(), "formulario_ativo.txt")

    fun marcarFormularioAtivo(tipo: String) {
        PlatformFiles.writeText(formularioAtivoPath, tipo)
    }

    fun carregarFormularioAtivo(): String? =
        PlatformFiles.readText(formularioAtivoPath)?.trim()?.takeIf { it.isNotEmpty() }
}

class ConfigStore {
    private val dir: String
        get() = PlatformFiles.join(PlatformFiles.documentsDir(), "app_config").also {
            PlatformFiles.ensureDir(it)
        }

    private val configPath: String
        get() = PlatformFiles.join(dir, "config.json")

    fun carregar(): AppConfig {
        val text = PlatformFiles.readText(configPath) ?: return AppConfig.padrao()
        return runCatching { FormJson.configFromJson(text) }.getOrDefault(AppConfig.padrao())
            .also { InspecaoCatalog.recarregar(it) }
    }

    suspend fun salvar(config: AppConfig): AppConfig {
        val ok = PlatformFiles.writeTextDurable(configPath, FormJson.configToJson(config))
        if (!ok) {
            error("Falha ao gravar configuração no armazenamento local")
        }
        InspecaoCatalog.recarregar(config)
        return config
    }

    fun exportarJsonPath(): String {
        val destino = PlatformFiles.join(PlatformFiles.cacheDir(), "Config_InspecaoCampo.json")
        PlatformFiles.writeText(destino, FormJson.configToJson(carregar()))
        return destino
    }

    suspend fun importarJson(text: String): AppConfig {
        val config = FormJson.configFromJson(text)
        return salvar(config)
    }
}

/** Relação EPI / C.A editável na aba Ficha de EPI. */
class EpiRelacaoStore {
    private val path: String
        get() = PlatformFiles.join(
            PlatformFiles.documentsDir().also { PlatformFiles.ensureDir(it) },
            "epi_relacao.json"
        )

    fun carregar(): List<br.com.htms.inspeccaocampo.data.epi.EpiRelacaoItem> {
        val text = PlatformFiles.readText(path) ?: return br.com.htms.inspeccaocampo.data.epi.EpiCatalog.seedRelacao()
        return br.com.htms.inspeccaocampo.data.epi.EpiRelacaoJson.fromJson(text)
            ?: br.com.htms.inspeccaocampo.data.epi.EpiCatalog.seedRelacao()
    }

    suspend fun salvar(lista: List<br.com.htms.inspeccaocampo.data.epi.EpiRelacaoItem>) {
        val ok = PlatformFiles.writeTextDurable(
            path,
            br.com.htms.inspeccaocampo.data.epi.EpiRelacaoJson.toJson(lista)
        )
        if (!ok) error("Falha ao gravar a relação de EPI")
        br.com.htms.inspeccaocampo.data.epi.EpiCatalog.definirRelacao(lista)
    }
}
